import { callFirebaseFunction } from 'boot/firebase';
import { Timestamp } from 'firebase/firestore';
import { chain, concat, filter, get, isDate, isEmpty, keyBy, map, transform } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { BROADCAST_MESSAGES } from 'src/constants/broadcastChannel';
import NOTIFICATION_TYPES from 'src/constants/notifications';
import AppointmentsDB from 'src/fb/appointments-db';
import ClientFilesDB from 'src/fb/clientFiles-db';
import ClientsDB from 'src/fb/clients-db';
import MessagesDB from 'src/fb/messages-db';
import ProgramsDB from 'src/fb/programs-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import UserNotificationsDB from 'src/fb/userNotifications-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import Auth from 'src/mixins/Auth';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { dataFields } from 'src/store/dataFields';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { notifications } from 'src/store/notifications';
import { BACKUP_DATA, BACKUP_NOTES } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';
import { getAttachmentContentDisposition } from 'src/utils/StorageUtils';

export default {
  mixins: [Auth, Interactions, Integrations, SessionExportMixin, Toolbox, Time],

  data() {
    return {
      sessionActivityCachedPrograms: {},

      deletedSessions: [],

      notificationTypesToSessionsStartPrivileges: {
        [NOTIFICATION_TYPES.PENDING_SIGNATURES]: 'sessions/startWithPendingSignaturesNotification',
        [NOTIFICATION_TYPES.PENDING_NOTES]: 'sessions/startWithPendingNotesNotification',
        [NOTIFICATION_TYPES.PENDING_ACTIVITIES]: 'sessions/startWithPendingActivitiesNotification',
        [NOTIFICATION_TYPES.PENDING_SESSION]: 'sessions/startWithPendingSessionNotification',
      },

      notificationTypesToDisableRequiredActionsPrivileges: {
        [NOTIFICATION_TYPES.PENDING_SIGNATURES]: 'disableRequiredActions/pendingSignatures',
        [NOTIFICATION_TYPES.PENDING_NOTES]: 'disableRequiredActions/pendingNotes',
        [NOTIFICATION_TYPES.PENDING_ACTIVITIES]: 'disableRequiredActions/pendingActivities',
        [NOTIFICATION_TYPES.PENDING_SESSION]: 'disableRequiredActions/pendingSession',
      },

      backupDataDocs: [],
      backupNotesDocs: [],
      noteValidationError: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'claims', 'currentClient',
      'isSupervisor', 'activityTypesSettings']),
    ...mapState(notifications, ['userActionNotifications']),
    ...mapState(dataFields, ['clientDataFields']),

    backupData() {
      return keyBy(this.backupDataDocs, 'id');
    },

    backupNotes() {
      return keyBy(this.backupNotesDocs, 'id');
    },

    canChangeSessionNotes() {
      return this.sessionActivity.user === this.userDetails.userId
          || this.userHasPrivilege('sessionActivities/completeOtherUsersNotes');
    },

    sessionActivityStatuses() {
      return {
        'in-progress': {
          label: this.$t('sessions.modals.sessionActivity.statuses.in-progress'),
          icon: 'play_arrow',
          color: 'primary',
        },
        finished: {
          label: this.$t('sessions.modals.sessionActivity.statuses.finished'),
          icon: 'pending_actions',
          color: 'warning',
        },
        'pending-review': {
          label: this.$t('sessions.modals.sessionActivity.statuses.pending-review'),
          icon: 'rate_review',
          color: 'warning',
        },
        'pending-signatures': {
          label: this.$t('sessions.modals.sessionActivity.statuses.pending-signatures'),
          icon: 'fas fa-file-signature',
          color: 'warning',
        },
        closed: {
          label: this.$t('sessions.modals.sessionActivity.statuses.closed'),
          icon: 'check_circle_outline',
          color: 'positive',
        },
        rendered: {
          label: this.$t(
            'sessions.modals.sessionActivity.statuses.rendered',
            { integration: this.getIntegrationName(get(this.connectedClinic, 'integration')) },
          ),
          icon: 'published_with_changes',
          color: 'primary',
          hidden: !this.connectedClinic.integration,
        },
        'integration-completed': {
          label: this.$t(
            'sessions.modals.sessionActivity.statuses.integrationCompleted',
            { integration: this.getIntegrationName(get(this.connectedClinic, 'integration')) },
          ),
          icon: 'published_with_changes',
          color: 'warning',
          hidden: !this.connectedClinic.integration,
        },
        other: {
          label: this.$t('sessions.modals.sessionActivity.statuses.unknown'),
          icon: 'unknown',
          color: 'primary',
          hidden: true,
        },
      };
    },

    userClientDataFields() {
      return filter(this.clientDataFields, { type: 'user' });
    },

    sessionActivityTypeOptions() {
      return Object.values(this.activityTypesSettings)
        .filter(({ isDeleted, visible }) => !isDeleted && visible !== false)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    },

    sessionActivityStatusOptions() {
      const omitStatuses = ['other'];
      if (!this.connectedClinic.integration) {
        omitStatuses.push('rendered');
      }

      return chain(this.sessionActivityStatuses)
        .omit(omitStatuses)
        .map(
          (sessionActivityStatus, key) => ({ value: key, ...sessionActivityStatus }),
        )
        .value();
    },
  },

  methods: {
    ...mapActions(firebaseUploader, ['addToUploadQueue']),

    async bindBackupsData(sessionId) {
      if (!sessionId) {
        return;
      }

      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
      await sessionBackupDataDb.bindAll(this, 'backupDataDocs', [['session', '==', sessionId]]);
    }, // bindBackups

    async bindBackupNotes(sessionId) {
      if (!sessionId) {
        return;
      }

      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_NOTES);
      await sessionBackupDataDb.bindAll(this, 'backupNotesDocs', [['session', '==', sessionId]]);
    }, // bindBackups

    async unbindBackupsData() {
      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
      await sessionBackupDataDb.unbind(this, 'backupDataDocs');
    }, // bindBackups

    async unbindBackupNotes() {
      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_NOTES);
      await sessionBackupDataDb.unbind(this, 'backupNotesDocs');
    }, // bindBackups

    async loadSessionDialogData(session) {
      let allPrograms = [];
      let client = null;
      if (!chain(this.sessionActivityCachedPrograms).get(session.client).isEmpty().value()) {
        allPrograms = get(this.sessionActivityCachedPrograms, session.client);
      } else {
        this.$q.loading.show();
        const programsDb = new ProgramsDB();
        allPrograms = await programsDb.readAll([
          ['client', '==', session.client],
        ]);
        this.sessionActivityCachedPrograms[session.client] = allPrograms;
        this.$q.loading.hide();
      }

      if (get(this.currentClient, 'id') === session.client) {
        client = this.currentClient;
      } else {
        const clientsDb = new ClientsDB();
        client = await clientsDb.read(session.client);
      }
      return {
        allPrograms,
        client,
      };
    },

    /**
     * @param {string} sessionId
     * @param {boolean?} hideLoader
     * @return {Promise<Object & {id: string} | null>}
     */
    async loadSessionDoc(sessionId, hideLoader = true) {
      if (this.deletedSessions.includes(sessionId)) {
        return null;
      }

      this.$q.loading.show();
      const sessionsDb = new SessionsDB();
      let session;

      try {
        session = await sessionsDb.read(sessionId);
        if (!session || get(session, 'isDeleted')) {
          this.$q.loading.hide();
          // Local cache for deleted sessions to reduce requests quantity
          this.deletedSessions.push(sessionId);
          return null;
        }
      } finally {
        if (hideLoader) {
          this.$q.loading.hide();
        }
      }

      return session;
    }, // loadSessionDoc()

    getBackupNotes(activityId, convertTimestamps = false) {
      const currentActivityNotes = get(this.backupNotes, activityId, null);

      if (!currentActivityNotes) {
        return null;
      }

      const { notes, fieldsData, isBackupAddedOnline } = currentActivityNotes;

      if (!notes && !fieldsData) {
        return null;
      }

      const transformDatesToTimestamps = (obj) => {
        return transform(obj, (result, value, key) => {
          if (isDate(value)) {
            if (convertTimestamps) {
              result[key] = value;
            } else {
              result[key] = Timestamp.fromDate(value);
            }
          } else if (typeof value === 'object' && value !== null) {
            // If the value is an object, recursively transform it
            result[key] = transformDatesToTimestamps(value);
          } else {
            // Otherwise, just copy the value
            result[key] = value;
          }
        });
      };

      return {
        notes,
        fieldsData: fieldsData ? transformDatesToTimestamps(fieldsData) : null,
        isBackupAddedOnline,
      };
    }, // getBackupNotes()

    getSessionActivityTypeIcon(sessionActivity) {
      return this.activityTypesSettings[sessionActivity.type]?.icon || this.activityTypesSettings.other.icon;
    },

    getSessionActivityTypeLabel(sessionActivity) {
      return this.activityTypesSettings[sessionActivity.type]?.label || this.activityTypesSettings.other.label;
    },

    getSessionActivityNoteTemplateLabel(sessionActivity) {
      return this.activityTypesSettings[sessionActivity.type]?.noteTemplateLabel
        || this.activityTypesSettings.other.noteTemplateLabel;
    },

    getSessionActivityNotesButtonLabel(sessionActivity) {
      const buttonType = sessionActivity.status === 'in-progress' ? 'editButton' : 'completeButton';
      return this.activityTypesSettings[sessionActivity.type]?.[buttonType]
        || this.activityTypesSettings.other[buttonType];
    },

    getSessionActivityStatusIcon(sessionActivity) {
      return get(
        this.sessionActivityStatuses,
        [sessionActivity.status, 'icon'],
        get(this.sessionActivityStatuses, ['other', 'icon']),
      );
    },

    getSessionActivityTypeAndStatusLabel(sessionActivity) {
      return `${get(
        this.sessionActivityStatuses,
        [sessionActivity.status, 'label'],
        get(this.sessionActivityStatuses, ['other', 'label']),
      )} (${this.getSessionActivityTypeLabel(sessionActivity)})`;
    },

    getSessionActivityStatusLabel(sessionActivity) {
      return get(
        this.sessionActivityStatuses,
        [sessionActivity.status, 'label'],
        get(this.sessionActivityStatuses, ['other', 'label']),
      );
    },

    getSessionActivityStatusAndAddendumLabel(sessionActivity) {
      const statusLabel = this.getSessionActivityStatusLabel(sessionActivity);

      if (!sessionActivity.hasAddendum || !sessionActivity.addendumStatus) {
        return statusLabel;
      }

      return this.$t(
        `addendums.activityStatusTooltip.${sessionActivity.addendumStatus}`,
        { status: statusLabel },
      );
    },

    getSessionActivityStatusColor(sessionActivity) {
      return get(
        this.sessionActivityStatuses,
        [sessionActivity.status, 'color'],
        get(this.sessionActivityStatuses, ['other', 'color']),
      );
    },

    getNewSessionActivityStatus(sessionActivity, changes) {
      const changesRequiredSignatories = get(changes, 'requiredSignatories', []);

      let requiredSignatories = [];
      if (get(changesRequiredSignatories, '_methodName') === 'arrayRemove') {
        // Remove requiredSignatories if they are firestore.arrayRemove'd()
        const removeRequiredSignatories = chain(changesRequiredSignatories)
          .omit('_methodName')
          .values()
          .flatMap((prop) => map(prop, 'uid'))
          .compact()
          .value();

        requiredSignatories = chain(sessionActivity)
          .get('requiredSignatories', [])
          .reject((requriedSignatory) => (removeRequiredSignatories.includes(requriedSignatory.uid)))
          .value();
      } else if (get(changesRequiredSignatories, '_methodName') === 'arrayUnion') {
        // Add requiredSignatories if they are firestore.arrayUnion'd()
        const addedRequiredSignatories = chain(changesRequiredSignatories)
          .omit('_methodName')
          .values()
          .flatMap((prop) => map(prop, 'uid'))
          .compact()
          .value();
        requiredSignatories = concat(
          get(sessionActivity, 'requiredSignatories', []),
          addedRequiredSignatories,
        );
      } else {
        requiredSignatories = concat(
          get(sessionActivity, 'requiredSignatories', []),
          get(changes, 'requiredSignatories', []),
        );
      }

      const newStatus = get(changes, 'status', sessionActivity.status);

      if (['pending-signatures', 'closed'].includes(newStatus)) {
        const updatedStatus = isEmpty(requiredSignatories) ? 'closed' : 'pending-signatures';

        if (
          updatedStatus === 'closed'
          && sessionActivity.status === 'pending-signatures'
          && sessionActivity.renderedTimestamp
        ) {
          return 'rendered';
        }

        return updatedStatus;
      }

      return newStatus;
    },

    /**
     * This method is used to prevent changing the status of a session activity to 'finished' when it shouldn't be.
     * @param {string} sessionActivityId
     * @param {string} stackTrace - @todo: remove this parameter when mobile source maps are fixed
     * @param {boolean?} [isReopening=false]
     * @returns {Promise<string>}
     */
    async applySessionActivityFinishedStatus(sessionActivityId, stackTrace, isReopening = false) {
      const baseLogContext = {
        sessionActivityId: sessionActivityId || null,
        stackTrace,
        isReopening,
        hasPrivilegeToReopen: this.userHasPrivilege('sessionActivities/reopen'),
      };

      const newStatus = 'finished';

      // If the session activity ID is not provided, we can't determine the current status, so we return the new status
      if (!sessionActivityId) {
        return newStatus;
      }

      // If the session activity is manually reopened, we don't need to check the current status
      if (isReopening) {
        return newStatus;
      }

      const sessionActivitiesDb = new SessionActivitiesDB();
      const sessionActivity = await sessionActivitiesDb.read(sessionActivityId);

      // If the session activity is not found, we can't determine the current status, so we return the new status
      if (!sessionActivity) {
        return newStatus;
      }

      // If the session activity is in progress, we can safely finish it
      if (sessionActivity.status === 'in-progress') {
        return newStatus;
      }

      // If the session activity isn't in progress, we log the status mismatch and return the current status
      logEvent('Session activity status mismatch', {
        severity: 'warning',
        context: {
          context_data: { ...baseLogContext, currentStatus: sessionActivity.status },
        },
      });

      return sessionActivity.status;
    },

    getSessionActivityDuration(sessionActivity) {
      const fromTimestamp = get(sessionActivity, 'startTimestamp');
      const toTimestamp = get(sessionActivity, 'endTimestamp');

      if (
        !(fromTimestamp instanceof Timestamp)
        || !(toTimestamp instanceof Timestamp)
      ) {
        return '';
      }

      const duration = (toTimestamp.toDate() - fromTimestamp.toDate()) / 1000;
      return this.formatDuration(duration);
    },

    async cancelSignatureRequestComments(signatory, sessionId = null, clientId = null) {
      if (sessionId === null) {
        return;
      }

      const messagesDb = new MessagesDB();

      const comments = await messagesDb.readAll([
        ['clinic', '==', this.userDetails.clinic],
        ['session', '==', sessionId],
        ['client', '==', clientId],
        ['type', '==', 'signatureRequest'],
        ['signatoryId', '==', signatory.uid],
      ]);

      if (!comments.length) {
        return;
      }

      return Promise.all(comments.map((comment) => messagesDb.delete(comment.id)));
    },

    async deleteSessionActivity(sessionActivity) {
      const cancelRequestActions = (sessionActivity?.requiredSignatories || [])
        .map((requiredSignatory) => this.cancelSignatureRequestComments(
          requiredSignatory,
          sessionActivity.session,
          sessionActivity.client,
        ));

      const sessionActivitiesDb = new SessionActivitiesDB();

      await Promise.all([
        sessionActivitiesDb.softDelete(sessionActivity.id),
        ...cancelRequestActions,
      ]);
    },

    /**
     * @param {Date} startTime
     * @param {Date} endTime
     * @param {Object} currentSessionActivity
     * @param {string} currentSessionActivityId
     * @return {Promise<boolean>}
     */
    async validateOverlappingActivities(startTime, endTime, currentSessionActivity, currentSessionActivityId) {
      if (this.userHasPrivilege('sessionActivities/allowOverlappingActivities')) {
        return true;
      }

      try {
        const sessionActivitiesDb = new SessionActivitiesDB();
        const sessionActivities = await sessionActivitiesDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['user', '==', currentSessionActivity.user],
            ['startTimestamp', '<', endTime],
          ],
          'startTimestamp',
          'desc',
          7,
        );

        const overlappingActivities = sessionActivities.filter((sessionActivity) => (
          !sessionActivity.isDeleted && sessionActivity.id !== currentSessionActivityId
                && (sessionActivity.startTimestamp instanceof Timestamp)
                && (sessionActivity.endTimestamp instanceof Timestamp)
                && (this.adjustDate(sessionActivity.startTimestamp, { second: 0, millisecond: 0 }) < endTime)
                && (this.adjustDate(sessionActivity.endTimestamp, { second: 0, millisecond: 0 }) > startTime)
                && (
                  (currentSessionActivity.client === null)
                    || ([null, currentSessionActivity.client].includes(sessionActivity.client))
                )
        ));

        if (isEmpty(overlappingActivities)) {
          return true;
        }

        // @todo This requires the parent component to have the method and should probably be refactored
        this.openOverlappingSessionActivitiesDialog(overlappingActivities);

        return false;
      } catch (err) {
        this.notifyNegative(err.message);
      }
    }, // validateOverlappingActivities()

    /**
     * We are trying to find the latest pending action notification that prevents session start.
     * We are skipping notifications with disabled required actions.
     * Since we bind the 5 latest pending action notifications during the application startup,
     * initially we try to find the latest pending action notification among them for optimization purposes.
     * If we don't find it among the already loaded notifications, then we try to find it in the database.
     */
    async findPendingActionNotificationThatPreventsSessionStart() {
      const notificationTypesWithMissingPrivileges = Object.entries(this.notificationTypesToSessionsStartPrivileges)
        .reduce((acc, [notificationType, privilege]) => {
          if (
            !this.userHasPrivilege(privilege)
            && !this.userHasPrivilege(this.notificationTypesToDisableRequiredActionsPrivileges[notificationType])
          ) {
            acc.push(notificationType);
          }

          return acc;
        }, []);

      // User has all required privileges to start session even if there are pending notifications
      if (!notificationTypesWithMissingPrivileges.length) {
        return null;
      }

      // User doesn't have pending notifications at all, se we can start session
      if (!this.userActionNotifications.length) {
        return null;
      }

      const pendingActionNotification = this.userActionNotifications.find((notification) => {
        return notificationTypesWithMissingPrivileges.includes(notification.type);
      });

      // Return the first notification that prevents session start from the already loaded notifications
      if (pendingActionNotification) {
        return pendingActionNotification;
      }

      const PENDING_NOTIFICATIONS_BIND_LIMIT = 5;

      // All pending notifications are loaded, but there are no notifications that prevent session start
      // So we can skip the database query
      if (this.userActionNotifications.length < PENDING_NOTIFICATIONS_BIND_LIMIT) {
        return null;
      }

      const userNotificationsDb = new UserNotificationsDB();

      // Try to load the first notification that prevents session start from the database if it wasn't loaded yet
      const pendingActionNotifications = await userNotificationsDb.readAll(
        [
          ['user', '==', this.userDetails.userId],
          ['type', 'in', notificationTypesWithMissingPrivileges],
          ['hasAction', '==', true],
          ['actionCompleted', '==', false],
        ],
        'createTimestamp',
        'desc',
        1,
      );

      if (!pendingActionNotifications.length) {
        return null;
      }

      return pendingActionNotifications[0];
    }, // findPendingActionNotificationThatPreventsSessionStart()

    async updateFilesClaimsIfAbsent(sessionActivityClient) {
      if (!this.isFileClaimsAdded(sessionActivityClient)) {
        try {
          await callFirebaseFunction('setFilesClaim', {
            clinic: this.userDetails.clinic,
            client: this.currentClient?.id || sessionActivityClient,
            role: this.userDetails.role,
          });
          this.sendBroadcastMessage(BROADCAST_MESSAGES.TOKEN_REVOCATION_BROADCAST_MESSAGE);
          await this.updateUserToken();
        } catch (err) {
          this.notifyNegative(this.$t('sessionAssets.asset.notifyAssetAccessError', { error: err.message }));
        }
      }
    },

    validateNoteContent(note) {
      const htmlContent = note || '';
      // Strip HTML tags
      const withoutTags = htmlContent.replace(/<[^>]*>/g, '');
      // Replace HTML whitespace entities (named and numeric) with actual spaces
      const textContent = withoutTags
        .replace(/&(nbsp|emsp|ensp|thinsp|#160|#32|#9|#8203);/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (textContent.length === 0) {
        this.noteValidationError = true;
        return false;
      }
      return true;
    }, // validateNoteContent()

    // @todo move this method to firebase and trigger on activity status update
    async saveFormToFile(sessionActivity, session) {
      if (!sessionActivity.clientFile) {
        return;
      }
      const clientFilesDB = new ClientFilesDB();
      const clientFile = await clientFilesDB.read(sessionActivity.clientFile);
      if (!clientFile) {
        // File doesn't exist
        return;
      }

      const { notes, signatures } = sessionActivity;
      const contentPages = [
        this.getSessionNotePdfPage([], notes, signatures),
      ];

      let appointment = null;
      if (sessionActivity.appointment) {
        const appointmentsDb = new AppointmentsDB();
        appointment = await appointmentsDb.read(sessionActivity.appointment) || null;
      }

      const layoutFieldsData = await this.getLayoutFieldsData({
        sessionActivity,
        session,
        appointment,
      });
      const file = await this.getSessionNotesPdf(
        contentPages,
        sessionActivity.clinic,
        sessionActivity.id,
        layoutFieldsData,
      );

      const uploadInfo = {
        destinationPath: `/clientFiles/${sessionActivity.clinic}/${sessionActivity.client}/`,
        destinationFilename: uid(),
        isBase64: true,
        file,
        metadata: {
          contentDisposition: getAttachmentContentDisposition(`${clientFile.filename}.${clientFile.ext}`),
          customMetadata: {
            type: 'form',
          },
          contentType: 'application/pdf',
        },
        callback: async (fileRef) => {
          const uploadedFileInfo = {
            srcFullPath: fileRef.fullPath,
            type: 'application/pdf',
          };
          await clientFilesDB.set(uploadedFileInfo, sessionActivity.clientFile);

          this.notifySuccess(
            this.$t(
              'sessions.modals.sessionActivity.notifyFileSaved',
              { fileName: `${clientFile.filename}.${clientFile.ext}` },
            ),
          );
        },
      };
      await this.updateFilesClaimsIfAbsent(sessionActivity.client);
      this.addToUploadQueue(uploadInfo);
    }, // saveFormToFile()

    isFileClaimsAdded(sessionActivityClient) {
      return (
        this.claims.clinic === this.userDetails.clinic
        && this.claims.client === (this.currentClient?.id || sessionActivityClient)
        && this.claims.role === this.userDetails.role
      );
    },

    async refreshLumaryAppointments(integration = '', linkedAppointment = null) {
      const DAY_IN_MS = 1000 * 60 * 60 * 24;

      if (integration === 'lumary' && linkedAppointment?.scheduledStartTimestamp) {
        await callFirebaseFunction('refreshAppointments', {
          clinicId: this.userDetails.clinic,
          objectType: 'client',
          objectId: this.currentClient.integrationReference,
          timeRange: {
            to: new Date(linkedAppointment.scheduledStartTimestamp.toMillis() + DAY_IN_MS),
            from: new Date(linkedAppointment.scheduledStartTimestamp.toMillis() - DAY_IN_MS),
          },
        });
      }
    },
    calcSignatureTimeDiscrepancy(sessionActivity, returnRawValue = false) {
      const toMillis = (value) => (value instanceof Timestamp ? value.toMillis() : value?.getTime?.() || null);

      const endMillis = toMillis(sessionActivity.endTimestamp);

      const [firstSignatureMillis] = (sessionActivity.signatures ?? [])
        .map((signature) => toMillis(signature?.timestamp))
        .filter((millis) => millis !== null)
        .sort((millisOne, millisTwo) => millisOne - millisTwo);

      if (endMillis === null || firstSignatureMillis === undefined) {
        return returnRawValue ? null : '';
      }

      const diffSec = Math.trunc((firstSignatureMillis - endMillis) / 1000);

      return returnRawValue ? diffSec : this.formatDuration(diffSec);
    },

  },

};
